// ========================================
// OAUTH CONFIGURATION
// ========================================
// Voor productie: Voeg deze credentials toe aan Firebase Environment Config
// Voor development: Gebruik .env.local file

// Detect if we're running on localhost - as a function for runtime evaluation
const getIsLocalhost = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// Get LinkedIn redirect URI dynamically
// BELANGRIJK: gebruik altijd het huidige origin zodat de callback terugkomt
// op hetzelfde domein waar de gebruiker ingelogd is (Firebase Auth is origin-scoped).
const getLinkedInRedirectUri = () => {
  if (typeof window === 'undefined') {
    return 'https://community-superkonnected-nl.web.app/oauth/linkedin/callback';
  }
  if (getIsLocalhost()) {
    let port = window.location.port;
    if (!port) {
      port = window.location.href.includes(':3001') ? '3001' : '3000';
    }
    const redirectUri = `http://localhost:${port}/oauth/linkedin/callback`;
    console.log('📍 LinkedIn Redirect URI:', redirectUri, '(Port:', port, ')');
    return redirectUri;
  }
  // Gebruik het exacte origin van de huidige pagina (bijv. https://community-superkonnected-nl.web.app
  // of https://www.superkonnected.com) zodat auth-sessie en sessionStorage behouden blijven.
  const redirectUri = `${window.location.origin}/oauth/linkedin/callback`;
  console.log('📍 LinkedIn Redirect URI:', redirectUri);
  return redirectUri;
};

const linkedInClientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '781boxk95ivzp0';

export const oauthConfig = {
  linkedin: {
    clientId: linkedInClientId,
    // client_secret is ONLY on backend (Firebase Cloud Functions)
    // NOT in frontend for security
    get redirectUri() { return getLinkedInRedirectUri(); },
    scopes: ['openid', 'profile', 'email', 'w_member_social'],
    // Note: Organization scopes (w_organization_social, r_organization_social) require:
    // - LinkedIn Marketing Partner status, OR
    // - Verified company with approved business case
    // Currently only w_member_social (personal posting) is available
    authUrl: 'https://www.linkedin.com/oauth/v2/authorization',
    tokenUrl: 'https://www.linkedin.com/oauth/v2/accessToken',
    apiUrl: 'https://api.linkedin.com/v2'
  },
  
  facebook: {
    appId: process.env.REACT_APP_FACEBOOK_APP_ID || '',
    appSecret: process.env.REACT_APP_FACEBOOK_APP_SECRET || '',
    redirectUri: process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://superkonnectedwebsite.web.app/oauth/facebook/callback'
      : 'http://localhost:3000/oauth/facebook/callback',
    scopes: ['public_profile', 'email', 'pages_manage_posts', 'pages_read_engagement'],
    authUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    tokenUrl: 'https://graph.facebook.com/v18.0/oauth/access_token',
    apiUrl: 'https://graph.facebook.com/v18.0'
  },
  
  twitter: {
    clientId: process.env.REACT_APP_TWITTER_CLIENT_ID || '',
    clientSecret: process.env.REACT_APP_TWITTER_CLIENT_SECRET || '',
    redirectUri: process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://community-superkonnected-nl.web.app/oauth/twitter/callback'
      : 'http://localhost:3000/oauth/twitter/callback',
    scopes: ['tweet.read', 'tweet.write', 'users.read', 'offline.access'],
    authUrl: 'https://twitter.com/i/oauth2/authorize',
    tokenUrl: 'https://api.twitter.com/2/oauth2/token',
    apiUrl: 'https://api.twitter.com/2'
  },
  
  instagram: {
    clientId: process.env.REACT_APP_INSTAGRAM_CLIENT_ID || '',
    clientSecret: process.env.REACT_APP_INSTAGRAM_CLIENT_SECRET || '',
    redirectUri: process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://superkonnectedwebsite.web.app/oauth/instagram/callback'
      : 'http://localhost:3000/oauth/instagram/callback',
    scopes: ['instagram_basic', 'instagram_content_publish'],
    authUrl: 'https://api.instagram.com/oauth/authorize',
    tokenUrl: 'https://api.instagram.com/oauth/access_token',
    apiUrl: 'https://graph.instagram.com'
  },
  
  pinterest: {
    appId: process.env.REACT_APP_PINTEREST_APP_ID || '',
    appSecret: process.env.REACT_APP_PINTEREST_APP_SECRET || '',
    redirectUri: process.env.REACT_APP_ENVIRONMENT === 'production'
      ? 'https://community-superkonnected-nl.web.app/oauth/pinterest/callback'
      : 'http://localhost:3000/oauth/pinterest/callback',
    scopes: ['pins:read', 'pins:write', 'boards:read', 'boards:write', 'user_accounts:read'],
    authUrl: 'https://www.pinterest.com/oauth/',
    tokenUrl: 'https://api.pinterest.com/v5/oauth/token',
    apiUrl: 'https://api.pinterest.com/v5'
  }
};

// Helper function to check if credentials are configured
export const isOAuthConfigured = (platform: keyof typeof oauthConfig): boolean => {
  switch (platform) {
    case 'linkedin':
      // Only check clientId in frontend, secret is on backend
      return !!(oauthConfig.linkedin.clientId);
    case 'facebook':
      return !!(oauthConfig.facebook.appId);
    case 'twitter':
      return !!(oauthConfig.twitter.clientId);
    case 'instagram':
      return !!(oauthConfig.instagram.clientId);
    case 'pinterest':
      return !!(oauthConfig.pinterest.appId);
    default:
      return false;
  }
};

// Generate random state for OAuth security
export const generateOAuthState = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export default oauthConfig;

